/**
 * ComplicationReport.js
 *
 * Surgical complication reporting form. Records complication type, severity,
 * onset time, clinical description, immediate actions, and notification status.
 * Shows previously reported complications for this surgery.
 *
 * Props:
 *   surgeryId - unique identifier for the current surgery
 */
import React, { useState, useEffect, useMemo } from 'react';
import { makeStyles } from '@material-ui/core/styles';
import Paper from '@material-ui/core/Paper';
import Typography from '@material-ui/core/Typography';
import Radio from '@material-ui/core/Radio';
import RadioGroup from '@material-ui/core/RadioGroup';
import FormControlLabel from '@material-ui/core/FormControlLabel';
import FormControl from '@material-ui/core/FormControl';
import Checkbox from '@material-ui/core/Checkbox';
import Snackbar from '@material-ui/core/Snackbar';
import Alert from '@material-ui/lab/Alert';
import Form from 'react-bootstrap/Form';
import Row from 'react-bootstrap/Row';
import Col from 'react-bootstrap/Col';
import TextField from '../../Utils/FormInputs/TextField';
import SelectInput from '../../Utils/FormInputs/SelectInput';
import Label from '../../Utils/FormInputs/Label';
import { ResizableTextarea } from '../../Utils/FormInputs/Textarea';
import { surgeryService } from '../../../services/surgeryService';
import { COMPLICATION_SEVERITY } from '../shared/constants';

const useStyles = makeStyles(() => ({
  root: {
    padding: '24px 28px',
    borderRadius: 8,
  },
  header: {
    marginBottom: 20,
  },
  title: {
    fontSize: '1.1rem',
    fontWeight: 600,
    color: '#212121',
    marginBottom: 2,
  },
  subtitle: {
    fontSize: '0.82rem',
    color: '#757575',
  },
  section: {
    marginBottom: 24,
  },
  sectionTitle: {
    fontSize: '0.7rem',
    fontWeight: 600,
    textTransform: 'uppercase',
    letterSpacing: '0.04em',
    color: '#9E9E9E',
    marginBottom: 14,
    paddingBottom: 8,
    borderBottom: '1px solid #F5F5F5',
  },
  alertBanner: {
    display: 'flex',
    alignItems: 'center',
    gap: 10,
    padding: '14px 20px',
    borderRadius: 8,
    marginBottom: 20,
  },
  alertBannerMajor: {
    backgroundColor: '#FFF3E0',
    border: '2px solid #E65100',
  },
  alertBannerLifeThreatening: {
    backgroundColor: '#FFEBEE',
    border: '2px solid #C62828',
  },
  alertBannerDeath: {
    backgroundColor: '#212121',
    border: '2px solid #000',
  },
  severityIndicator: {
    width: 14,
    height: 14,
    borderRadius: '50%',
    display: 'inline-block',
    marginRight: 6,
    verticalAlign: 'middle',
  },
  previousSection: {
    border: '1px solid #EEEEEE',
    borderRadius: 8,
    padding: '16px 20px',
    backgroundColor: '#FAFAFA',
    marginTop: 24,
  },
  previousItem: {
    padding: '12px 0',
    borderBottom: '1px solid #EEEEEE',
    '&:last-child': {
      borderBottom: 'none',
    },
  },
  previousTitle: {
    fontSize: '0.85rem',
    fontWeight: 600,
    color: '#212121',
  },
  previousDetail: {
    fontSize: '0.82rem',
    color: '#757575',
    marginTop: 4,
  },
  severityBadge: {
    display: 'inline-block',
    padding: '2px 8px',
    borderRadius: 4,
    fontSize: '0.72rem',
    fontWeight: 600,
    color: '#fff',
  },
  footer: {
    display: 'flex',
    justifyContent: 'flex-end',
    paddingTop: 16,
    borderTop: '1px solid #EEEEEE',
    marginTop: 8,
  },
}));

const COMPLICATION_TYPE_OPTIONS = [
  { value: '', text: 'Select type' },
  { value: 'Hemorrhage', text: 'Hemorrhage' },
  { value: 'Infection', text: 'Infection' },
  { value: 'Organ Injury', text: 'Organ Injury' },
  { value: 'Anaesthesia Complication', text: 'Anaesthesia Complication' },
  { value: 'DVT/PE', text: 'DVT/PE' },
  { value: 'Wound Dehiscence', text: 'Wound Dehiscence' },
  { value: 'Other', text: 'Other' },
];

const SEVERITY_OPTIONS = [
  { value: COMPLICATION_SEVERITY.MINOR, label: 'Minor', color: '#4CAF50' },
  { value: COMPLICATION_SEVERITY.MODERATE, label: 'Moderate', color: '#FF9800' },
  { value: COMPLICATION_SEVERITY.MAJOR, label: 'Major', color: '#E65100' },
  { value: COMPLICATION_SEVERITY.LIFE_THREATENING, label: 'Life-threatening', color: '#C62828' },
  { value: COMPLICATION_SEVERITY.DEATH, label: 'Death', color: '#212121' },
];

const getSeverityColor = (severity) => {
  const opt = SEVERITY_OPTIONS.find((o) => o.value === severity);
  return opt ? opt.color : '#999';
};

const getSeverityLabel = (severity) => {
  const opt = SEVERITY_OPTIONS.find((o) => o.value === severity);
  return opt ? opt.label : severity;
};

const ComplicationReport = ({ surgeryId }) => {
  const classes = useStyles();

  // Form state
  const [complicationType, setComplicationType] = useState('');
  const [severity, setSeverity] = useState('');
  const [onsetTime, setOnsetTime] = useState('');
  const [description, setDescription] = useState('');
  const [immediateActions, setImmediateActions] = useState('');
  const [reportedBy, setReportedBy] = useState('');
  const [surgeonNotified, setSurgeonNotified] = useState(false);
  const [surgeonNotifiedTime, setSurgeonNotifiedTime] = useState('');
  const [additionalInvestigations, setAdditionalInvestigations] = useState(false);
  const [investigationDetails, setInvestigationDetails] = useState('');

  const [previousComplications, setPreviousComplications] = useState([]);

  const loadComplications = () => {
    if (!surgeryId) return;
    surgeryService.listComplications(surgeryId)
      .then((res) => {
        const list = Array.isArray(res?.data) ? res.data : [];
        setPreviousComplications(list);
      })
      .catch(() => {});
  };

  useEffect(() => { loadComplications(); }, [surgeryId]);

  const [loading, setLoading] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });

  const showSnackbar = (message, sev = 'success') => {
    setSnackbar({ open: true, message, severity: sev });
  };

  const isSevereComplication = useMemo(() => {
    return [
      COMPLICATION_SEVERITY.MAJOR,
      COMPLICATION_SEVERITY.LIFE_THREATENING,
      COMPLICATION_SEVERITY.DEATH,
    ].includes(severity);
  }, [severity]);

  const getAlertBannerClass = () => {
    if (severity === COMPLICATION_SEVERITY.DEATH) return classes.alertBannerDeath;
    if (severity === COMPLICATION_SEVERITY.LIFE_THREATENING) return classes.alertBannerLifeThreatening;
    return classes.alertBannerMajor;
  };

  const getAlertTextColor = () => {
    if (severity === COMPLICATION_SEVERITY.DEATH) return '#fff';
    if (severity === COMPLICATION_SEVERITY.LIFE_THREATENING) return '#C62828';
    return '#E65100';
  };

  const handleSave = async () => {
    setSubmitted(true);
    if (!complicationType || !severity) {
      showSnackbar('Please select complication type and severity', 'error');
      return;
    }
    setLoading(true);
    try {
      await surgeryService.reportComplication({
        surgery_id: surgeryId,
        complication_type: complicationType,
        severity,
        onset_time: onsetTime,
        description,
        immediate_actions: immediateActions,
        reported_by: reportedBy,
        surgeon_notified: surgeonNotified,
        surgeon_notified_time: surgeonNotifiedTime,
        additional_investigations: additionalInvestigations,
        investigation_details: investigationDetails,
        reported_at: new Date().toISOString(),
      });
      showSnackbar('Complication reported successfully');
      loadComplications();
      // Clear form
      setComplicationType('');
      setSeverity('');
      setOnsetTime('');
      setDescription('');
      setImmediateActions('');
      setSurgeonNotified(false);
      setSurgeonNotifiedTime('');
      setAdditionalInvestigations(false);
      setInvestigationDetails('');
      setSubmitted(false);
    } catch (err) {
      showSnackbar(err.message || 'Failed to report complication', 'error');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Paper className={classes.root} elevation={1}>
      <div className={classes.header}>
        <Typography className={classes.title}>Complication Report</Typography>
        <Typography className={classes.subtitle}>
          Report surgical complications &mdash; Surgery #{surgeryId}
        </Typography>
      </div>

      {/* Severity Alert Banner */}
      {isSevereComplication && (
        <div className={`${classes.alertBanner} ${getAlertBannerClass()}`}>
          <Typography style={{ fontSize: '0.88rem', fontWeight: 600, color: getAlertTextColor() }}>
            This complication will be flagged for review by the surgical quality committee
          </Typography>
        </div>
      )}

      {/* Complication Details */}
      <div className={classes.section}>
        <Typography className={classes.sectionTitle}>Complication Details</Typography>

        <Row>
          <Col xs={12} md={6}>
            <Form.Group>
              <Label name="Complication Type" type />
              <SelectInput
                name="complicationType"
                value={complicationType}
                onChange={(e) => setComplicationType(e.target.value)}
                options={COMPLICATION_TYPE_OPTIONS}
                submitted={submitted}
                disabled={loading}
              />
            </Form.Group>
          </Col>
          <Col xs={12} md={6}>
            <Form.Group>
              <Label name="Time of Onset" />
              <TextField
                name="onsetTime"
                type="datetime-local"
                value={onsetTime}
                onChange={(e) => setOnsetTime(e.target.value)}
                disabled={loading}
              />
            </Form.Group>
          </Col>
        </Row>

        <Form.Group>
          <Label name="Severity" type />
          <FormControl component="fieldset">
            <RadioGroup
              row
              value={severity}
              onChange={(e) => setSeverity(e.target.value)}
            >
              {SEVERITY_OPTIONS.map((opt) => (
                <FormControlLabel
                  key={opt.value}
                  value={opt.value}
                  disabled={loading}
                  control={<Radio size="small" style={{ color: opt.color }} />}
                  label={
                    <span style={{ fontSize: '0.82rem', display: 'flex', alignItems: 'center', gap: 4 }}>
                      <span
                        className={classes.severityIndicator}
                        style={{ backgroundColor: opt.color }}
                      />
                      {opt.label}
                    </span>
                  }
                />
              ))}
            </RadioGroup>
          </FormControl>
        </Form.Group>

        <Form.Group>
          <Label name="Clinical Description" />
          <ResizableTextarea
            rows={3}
            name="description"
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            placeholder="Describe the complication, including relevant clinical findings, vital sign changes, and timeline of events."
            disabled={loading}
          />
        </Form.Group>

        <Form.Group>
          <Label name="Immediate Actions" />
          <ResizableTextarea
            rows={3}
            name="immediateActions"
            value={immediateActions}
            onChange={(e) => setImmediateActions(e.target.value)}
            placeholder="e.g. Applied pressure to wound site. Administered 500ml Normal Saline bolus."
            disabled={loading}
          />
        </Form.Group>
      </div>

      {/* Reporting & Notification */}
      <div className={classes.section}>
        <Typography className={classes.sectionTitle}>Reporting & Notification</Typography>

        <Form.Group>
          <Label name="Reported By" />
          <TextField
            name="reportedBy"
            type="text"
            value={reportedBy}
            onChange={(e) => setReportedBy(e.target.value)}
            placeholder="e.g. Dr. Smith"
            disabled={loading}
          />
        </Form.Group>

        <Form.Group className="mb-2">
          <Label name="Surgeon Notified" />
          <div style={{ display: 'flex', alignItems: 'center', gap: 16 }}>
            <FormControlLabel
              control={
                <Checkbox
                  checked={surgeonNotified}
                  onChange={(e) => setSurgeonNotified(e.target.checked)}
                  color="primary"
                  size="small"
                  disabled={loading}
                />
              }
              label={<span style={{ fontSize: '0.82rem' }}>Attending surgeon notified</span>}
            />
            {surgeonNotified && (
              <div style={{ width: 160 }}>
                <TextField
                  name="surgeonNotifiedTime"
                  type="time"
                  value={surgeonNotifiedTime}
                  onChange={(e) => setSurgeonNotifiedTime(e.target.value)}
                  disabled={loading}
                />
              </div>
            )}
          </div>
        </Form.Group>

        <Form.Group>
          <Label name="Investigations" />
          <FormControlLabel
            control={
              <Checkbox
                checked={additionalInvestigations}
                onChange={(e) => setAdditionalInvestigations(e.target.checked)}
                color="primary"
                size="small"
                disabled={loading}
              />
            }
            label={<span style={{ fontSize: '0.82rem' }}>Additional investigations ordered</span>}
          />
          {additionalInvestigations && (
            <div style={{ marginTop: 8 }}>
              <ResizableTextarea
                rows={2}
                name="investigationDetails"
                value={investigationDetails}
                onChange={(e) => setInvestigationDetails(e.target.value)}
                placeholder="e.g. Urgent FBC, Group & Cross-match, CT scan abdomen"
                disabled={loading}
              />
            </div>
          )}
        </Form.Group>
      </div>

      {/* Footer */}
      <div className={classes.footer}>
        <button
          type="button"
          className="btn btn-sm sms-btn px-4"
          onClick={handleSave}
          disabled={loading}
        >
          {loading ? 'Submitting...' : 'Submit Complication Report'}
        </button>
      </div>

      {/* Previously Reported Complications */}
      {previousComplications.length > 0 && (
        <div className={classes.previousSection}>
          <Typography className={classes.sectionTitle}>
            Previous Complications for This Surgery
          </Typography>
          {previousComplications.map((comp) => (
            <div className={classes.previousItem} key={comp.complication_id}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 4 }}>
                <Typography className={classes.previousTitle}>
                  {comp.complication_type}
                </Typography>
                <span
                  className={classes.severityBadge}
                  style={{ backgroundColor: getSeverityColor(comp.severity) }}
                >
                  {getSeverityLabel(comp.severity)}
                </span>
              </div>
              {comp.description && (
                <Typography className={classes.previousDetail}>
                  {comp.description}
                </Typography>
              )}
              <Typography style={{ fontSize: '0.75rem', color: '#9E9E9E', marginTop: 4 }}>
                {comp.reported_by ? `Reported by ${comp.reported_by}` : 'Reported'}{' '}
                {comp.identified_at ? `-- ${new Date(comp.identified_at).toLocaleString('en-US', {
                  day: '2-digit', month: 'short', year: 'numeric',
                  hour: '2-digit', minute: '2-digit', hour12: false,
                })}` : ''}
              </Typography>
            </div>
          ))}
        </div>
      )}

      {/* Snackbar */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={4000}
        onClose={() => setSnackbar((s) => ({ ...s, open: false }))}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert
          onClose={() => setSnackbar((s) => ({ ...s, open: false }))}
          severity={snackbar.severity}
          elevation={6}
          variant="filled"
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Paper>
  );
};

export default ComplicationReport;
